import type { DocumentReference } from '../services/schemas/loanServiceSchemas/documentReference';
import type { DocumentReferenceDocumentType } from '../services/schemas/loanServiceSchemas/documentReferenceDocumentType';
import type { VerificationItem } from '../services/schemas/loanServiceSchemas/verificationItem';

/**
 * Verification checks, shared by the verification screen and everyone who reads its outcome.
 */

/**
 * The documents an officer reads to rule on each check. The first type is the one an upload is
 * filed under.
 *
 * Documents used to show only on checks flagged requiresUpload, which the backend sets on the
 * purchase order alone -- so the officer asked to confirm a company registration or a director's
 * identity had no document in front of them. Director and representative ID copies count toward
 * identity, since that is where a company's directors' IDs are filed.
 */
export const CHECK_DOCUMENT_TYPES: Record<string, DocumentReferenceDocumentType[]> = {
  ID_DOC: ['ID_DOCUMENT', 'DIRECTOR_ID_COPY', 'COMPANY_REP_ID_COPY'],
  PHOTO_MATCH: ['ID_DOCUMENT', 'DIRECTOR_ID_COPY', 'COMPANY_REP_ID_COPY'],
  BANK_STATEMENTS: ['BANK_STATEMENT'],
  ACCOUNT_HOLDER: ['BANK_LETTER', 'BANK_STATEMENT'],
  ACCOUNT_STATUS: ['BANK_LETTER', 'BANK_STATEMENT'],
  REGISTRATION: ['COMPANY_REGISTRATION_CERTIFICATE', 'CSD_REPORT'],
  TAX_COMPLIANCE: ['TAX_CLEARANCE'],
  BUSINESS_ADDRESS: ['PROOF_OF_ADDRESS'],
  PURCHASE_ORDER: ['PURCHASE_ORDER', 'PO_VERIFICATION'],
  AWARD_LETTER: ['AWARD_LETTER'],
  ASSET_EXISTENCE: ['COLLATERAL_DOCUMENT'],
  ASSET_VALUE: ['ASSET_VALUATION', 'COLLATERAL_DOCUMENT'],
  // Ownership documents uploaded on the documents page before verification (finding #13)
  OWNERSHIP_DOCS: ['COLLATERAL_DOCUMENT', 'ASSET_OWNERSHIP_DOCUMENT', 'PROPERTY_DOCUMENT'],
  AFFORDABILITY: ['AFFORDABILITY_ASSESSMENT'],
  // The bureau report is no longer uploaded (finding #17); the director's consent remains
  CREDIT_CHECK: ['DIRECTOR_CONSENT'],
  PROOF_OF_CONTRACT: ['PROOF_OF_CONTRACT']
};

// Older API builds sent no code, only the check's label. Kept so those responses still resolve.
const LABEL_TO_CODE: Record<string, string> = {
  'ID Document': 'ID_DOC',
  'Photo Matching': 'PHOTO_MATCH',
  'Bank Statements': 'BANK_STATEMENTS',
  'Account Holder Verification': 'ACCOUNT_HOLDER',
  'Account Status': 'ACCOUNT_STATUS',
  'Purchase Order': 'PURCHASE_ORDER',
  'Letter of Award': 'AWARD_LETTER',
  'Asset Existence': 'ASSET_EXISTENCE',
  'Asset Valuation': 'ASSET_VALUE',
  'Ownership Documents': 'OWNERSHIP_DOCS',
  'Company Registration': 'REGISTRATION',
  'Tax Compliance': 'TAX_COMPLIANCE',
  'Business Address': 'BUSINESS_ADDRESS',
  'Affordability Assessment': 'AFFORDABILITY',
  'Director Credit Checks': 'CREDIT_CHECK',
  'Criminal Record Check': 'CRIMINAL_RECORD'
};

const CODE_PATTERN = /^[A-Z_]+$/;

/** The check's code, e.g. "REGISTRATION"; the backend addresses a check by it. */
export const getCheckCode = (item: Pick<VerificationItem, 'id' | 'type' | 'label'>): string => {
  if (item.type && CODE_PATTERN.test(item.type)) return item.type;
  if (item.id && CODE_PATTERN.test(item.id)) return item.id;
  if (item.label && LABEL_TO_CODE[item.label]) return LABEL_TO_CODE[item.label];
  return item.type || item.id || '';
};

/** Every current document on file for a check, e.g. each director's ID copy for identity. */
export const getCheckDocuments = (
  checkCode: string,
  documents?: DocumentReference[] | null
): DocumentReference[] => {
  const documentTypes = CHECK_DOCUMENT_TYPES[checkCode];
  if (!documents || !documentTypes) return [];
  return documents.filter(doc => doc.documentType && documentTypes.includes(doc.documentType));
};
